import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    FormControlLabel,
    Switch,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Grid,
    Chip,
    createFilterOptions,
} from '@mui/material';
import {
    Campaign as CampaignIcon,
    FlashOn as FlashOnIcon,
} from '@mui/icons-material';
import PotvrzeniVyhlaseniDialog from './PotvrzeniVyhlaseniDialog';
import {
    useAllPodtypyQuery,
    useTypyUdalostiQuery,
    useCreateUdalostMutation,
    useAllUsersQuery,
    useSablonyQuery,
    useVyhlasitZeSablonyMutation,
} from '../api/JsdhNotifikaceService/service/AdminMutations';
import type { PodtypUdalosti, TypUdalosti, SablonaUdalosti } from '../api/JsdhNotifikaceService/generated';
import UserSelector from './UserSelector';

interface RychlyPoplachDialogProps {
    open: boolean;
    onClose: () => void;
    initialSablona?: SablonaUdalosti;
}

interface PodtypOption extends PodtypUdalosti {
    typNazev: string;
}

// Custom filter that searches in both podtyp name and type name
const filterOptions = createFilterOptions<PodtypOption>({
    stringify: (option) => `${option.nazev} ${option.typNazev}`,
});

const RychlyPoplachDialog: React.FC<RychlyPoplachDialogProps> = ({ open, onClose, initialSablona }) => {
    const { data: podtypy, isLoading: isLoadingPodtypy } = useAllPodtypyQuery();
    const { data: typy, isLoading: isLoadingTypy } = useTypyUdalostiQuery();
    const { data: allUsers = [], isLoading: isLoadingUsers } = useAllUsersQuery();
    const { data: sablony = [] } = useSablonyQuery();
    const createMutation = useCreateUdalostMutation();
    const vyhlasitZeSablonyMutation = useVyhlasitZeSablonyMutation();

    const [potvrzeniSablona, setPotvrzeniSablona] = useState<SablonaUdalosti | null>(null);
    const [selectedPodtyp, setSelectedPodtyp] = useState<PodtypOption | null>(null);
    const [selectedUserIds, setSelectedUserIds] = useState<number[] | null>(null); // null = všem
    const [popis, setPopis] = useState('');
    // Adresa
    const [obec, setObec] = useState('');
    const [kraj, setKraj] = useState('');
    const [cast, setCast] = useState('');
    const [ulice, setUlice] = useState('');
    const [cisloPopisne, setCisloPopisne] = useState('');
    const [objekt, setObjekt] = useState('');
    const [gps, setGps] = useState('');
    const [upresneni, setUpresneni] = useState('');
    // Nastavení
    const [odeslatNotifikace, setOdeslatNotifikace] = useState(true);
    const [generovatTts, setGenerovatTts] = useState(true);

    // Combine podtypy with type names for display
    const podtypyOptions = useMemo<PodtypOption[]>(() => {
        if (!podtypy || !typy) return [];

        const typyMap = new Map<number, TypUdalosti>();
        typy.forEach((typ) => typyMap.set(typ.id, typ));

        return podtypy.map((podtyp) => ({
            ...podtyp,
            typNazev: typyMap.get(podtyp.typId)?.nazev || 'Neznámý typ',
        }));
    }, [podtypy, typy]);

    // Pre-fill from initialSablona when dialog opens
    useEffect(() => {
        if (open && initialSablona && podtypyOptions.length > 0) {
            handleSablonaSelect(initialSablona);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialSablona, podtypyOptions.length]);

    const handleSubmit = () => {
        if (!selectedPodtyp || !obec || !popis) return;

        createMutation.mutate(
            {
                typId: selectedPodtyp.typId,
                podtypId: selectedPodtyp.id,
                popis,
                obec,
                kraj: kraj || undefined,
                cast: cast || undefined,
                ulice: ulice || undefined,
                cisloPopisne: cisloPopisne || undefined,
                objekt: objekt || undefined,
                gps: gps || undefined,
                upresneni: upresneni || undefined,
                odeslatNotifikace,
                generovatTts,
                userIds: odeslatNotifikace ? selectedUserIds : undefined,
            },
            {
                onSuccess: () => {
                    handleClose();
                },
            }
        );
    };

    const handleClose = () => {
        setSelectedPodtyp(null);
        setSelectedUserIds(null);
        setPopis('');
        setObec('');
        setKraj('');
        setCast('');
        setUlice('');
        setCisloPopisne('');
        setObjekt('');
        setGps('');
        setUpresneni('');
        setOdeslatNotifikace(true);
        setGenerovatTts(true);
        onClose();
    };

    const handleSablonaClick = (sablona: SablonaUdalosti) => {
        if (sablona.rychleVyhlaseni) {
            setPotvrzeniSablona(sablona);
            return;
        }
        handleSablonaSelect(sablona);
    };

    const handleSablonaSelect = (sablona: SablonaUdalosti) => {
        // Pre-fill form from template
        if (sablona.podtypId) {
            const podtypOption = podtypyOptions.find((p) => p.id === sablona.podtypId);
            if (podtypOption) setSelectedPodtyp(podtypOption);
        }
        if (sablona.popis) setPopis(sablona.popis);
        if (sablona.obec) setObec(sablona.obec);
        if (sablona.kraj) setKraj(sablona.kraj);
        if (sablona.castObce) setCast(sablona.castObce);
        setUlice(sablona.ulice || '');
        if (sablona.cisloPopisne) setCisloPopisne(sablona.cisloPopisne);
        if (sablona.objekt) setObjekt(sablona.objekt);
        if (sablona.gps) setGps(sablona.gps);
        if (sablona.upresneni) setUpresneni(sablona.upresneni);
        if (sablona.odeslatNotifikace !== undefined) setOdeslatNotifikace(sablona.odeslatNotifikace);
        if (sablona.generovatTts !== undefined) setGenerovatTts(sablona.generovatTts);
    };

    const handlePodtypChange = (_: React.SyntheticEvent, value: PodtypOption | null) => {
        setSelectedPodtyp(value);
        if (value) {
            // Pre-fill popis with podtyp name
            setPopis(value.nazev);
        }
    };

    const isLoading = isLoadingPodtypy || isLoadingTypy;
    const isUserSelectionInvalid = odeslatNotifikace && selectedUserIds !== null && selectedUserIds.length === 0;
    const canSubmit = selectedPodtyp && obec.trim() && popis.trim() && !createMutation.isPending && !isUserSelectionInvalid;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <FlashOnIcon color="warning" />
                    <Typography variant="h6">Rychlé vyhlášení poplachu</Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} pt={1}>
                    {/* Šablony */}
                    {sablony.length > 0 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                Načíst ze šablony:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                {sablony.map((s) => (
                                    <Chip
                                        key={s.id}
                                        label={s.nazev}
                                        onClick={() => handleSablonaClick(s)}
                                        icon={s.rychleVyhlaseni ? <FlashOnIcon sx={{ fontSize: 16 }} /> : undefined}
                                        sx={{
                                            bgcolor: s.barva || undefined,
                                            color: s.barva ? '#fff' : undefined,
                                            '&:hover': {
                                                bgcolor: s.barva || undefined,
                                                opacity: 0.85,
                                            },
                                            '& .MuiChip-icon': { color: 'inherit' },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {createMutation.isError && (
                        <Alert severity="error">
                            {createMutation.error?.message || 'Nepodařilo se vyhlásit poplach'}
                        </Alert>
                    )}

                    {/* Typ události */}
                    <Autocomplete
                        options={podtypyOptions}
                        value={selectedPodtyp}
                        onChange={handlePodtypChange}
                        loading={isLoading}
                        filterOptions={filterOptions}
                        getOptionLabel={(option) => option.nazev}
                        groupBy={(option) => option.typNazev}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Typ události"
                                placeholder="Začněte psát pro vyhledání..."
                                required
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Box>
                                    <Typography variant="body1">{option.nazev}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.typNazev}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        noOptionsText="Žádné typy nenalezeny"
                        loadingText="Načítám..."
                    />

                    {/* Popis */}
                    <TextField
                        label="Popis události"
                        value={popis}
                        onChange={(e) => setPopis(e.target.value)}
                        required
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="např. Požár rodinného domu"
                    />

                    {/* Adresa - Grid layout */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                        Adresa
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Obec"
                                value={obec}
                                onChange={(e) => setObec(e.target.value)}
                                required
                                fullWidth
                                placeholder="např. Praha"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Kraj"
                                value={kraj}
                                onChange={(e) => setKraj(e.target.value)}
                                fullWidth
                                placeholder="např. Středočeský"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Část obce"
                                value={cast}
                                onChange={(e) => setCast(e.target.value)}
                                fullWidth
                                placeholder="např. Staré Město"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Ulice"
                                value={ulice}
                                onChange={(e) => setUlice(e.target.value)}
                                fullWidth
                                placeholder="např. Palackého"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Číslo popisné"
                                value={cisloPopisne}
                                onChange={(e) => setCisloPopisne(e.target.value)}
                                fullWidth
                                placeholder="např. 123"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Objekt"
                                value={objekt}
                                onChange={(e) => setObjekt(e.target.value)}
                                fullWidth
                                placeholder="např. Základní škola"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="GPS souřadnice"
                                value={gps}
                                onChange={(e) => setGps(e.target.value)}
                                fullWidth
                                placeholder="např. 50.0755,14.4378"
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                label="Upřesnění"
                                value={upresneni}
                                onChange={(e) => setUpresneni(e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                placeholder="např. u hlavní silnice, za kostelem"
                            />
                        </Grid>
                    </Grid>

                    {/* Nastavení */}
                    <Box display="flex" gap={2} mt={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={odeslatNotifikace}
                                    onChange={(e) => setOdeslatNotifikace(e.target.checked)}
                                />
                            }
                            label="Odeslat notifikace"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={generovatTts}
                                    onChange={(e) => setGenerovatTts(e.target.checked)}
                                />
                            }
                            label="Generovat TTS"
                        />
                    </Box>

                    {/* Výběr příjemců */}
                    {odeslatNotifikace && (
                        <UserSelector
                            users={allUsers}
                            isLoading={isLoadingUsers}
                            selectedUserIds={selectedUserIds}
                            onSelectionChange={setSelectedUserIds}
                            disabled={createMutation.isPending}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={createMutation.isPending}>
                    Zrušit
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    startIcon={createMutation.isPending ? <CircularProgress size={20} /> : <CampaignIcon />}
                >
                    {createMutation.isPending ? 'Vyhlašuji...' : 'VYHLÁSIT POPLACH'}
                </Button>
            </DialogActions>

            <PotvrzeniVyhlaseniDialog
                open={!!potvrzeniSablona}
                onClose={() => setPotvrzeniSablona(null)}
                sablona={potvrzeniSablona}
                onSuccess={handleClose}
            />
        </Dialog>
    );
};

export default RychlyPoplachDialog;
